import React, { useState, useEffect } from "react";
import {
  KeyboardArrowUp,
  KeyboardArrowRight,
  Menu,
  Close,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";

interface MenuItem {
  label: string;
  path?: string;
  children?: { label: string; path: string }[];
}

const menuItems: MenuItem[] = [
  { label: "Home", path: "/" },
  { label: "Database Search", path: "/database-search" },
  {
    label: "Knowledge Hub",
    children: [
      {
        label: "Getting Started",
        path: "/knowledge-hub/getting-started/commercials",
      },
      { label: "FAQ", path: "/knowledge-hub/faq/database" },
      {
        label: "Database Overview",
        path: "/knowledge-hub/database-overview/overview",
      },
      { label: "Data Quality", path: "/knowledge-hub/data-quality" },
      { label: "Toolbox", path: "/knowledge-hub/toolbox/overview" },
      {
        label: "Releases",
        path: "/knowledge-hub/releases/releases-and-changes",
      },
    ],
  },
  {
    label: "API Web Reference",
    children: [
      { label: "Overview", path: "/api-web-reference/overview" },
      { label: "Reference", path: "/api-web-reference/reference" },
    ],
  },
];

const NavigationMenu: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<{
    [key: string]: boolean;
  }>({});
  const location = useLocation();

  useEffect(() => {
    const initialExpandedState = menuItems.reduce((acc, item) => {
      if (item.children) {
        const isActive = item.children.some((child) =>
          location.pathname.startsWith(
            child.path.split("/").slice(0, -1).join("/")
          )
        );
        if (isActive) {
          acc[item.label] = true;
        }
      }
      return acc;
    }, {} as { [key: string]: boolean });
    setExpandedMenus(initialExpandedState);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSubMenu = (label: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <>
      {/* Hamburger Menu Button */}
      <button
        onClick={toggleMenu}
        className="fixed top-4 left-4 md:hidden z-[100] text-gray-800 bg-white rounded-full p-1 shadow"
      >
        {isMenuOpen ? <Close fontSize="large" /> : <Menu fontSize="large" />}
      </button>

      {/* Overlay to close menu when clicked outside */}
      {isMenuOpen && (
        <div
          onClick={toggleMenu}
          className="fixed inset-0 bg-black opacity-50 z-40 md:hidden"
        ></div>
      )}

      {/* Navigation Menu */}
      <div
        className={`fixed top-15 text-left left-0 h-screen w-64 max-w-full sm:max-w-xs bg-white shadow-lg p-6 transform ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out md:translate-x-0 z-50`}
      >
        <nav className="flex flex-col space-y-4">
          {menuItems.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <button
                    onClick={() => toggleSubMenu(item.label)}
                    className="flex justify-between items-center text-gray-800 font-semibold hover:bg-gray-100 p-2 w-full rounded-md"
                  >
                    <span>{item.label}</span>
                    {expandedMenus[item.label] ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowRight />
                    )}
                  </button>
                  {expandedMenus[item.label] && (
                    <ul className="pl-4 mt-2 space-y-2">
                      {item.children.map((child) => (
                        <li key={child.label}>
                          <a
                            href={child.path}
                            className={`block text-gray-700 hover:bg-gray-100 hover:text-gray-900 p-1 rounded-md ${
                              location.pathname === child.path
                                ? "font-bold"
                                : ""
                            }`}
                          >
                            {child.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <a
                  href={item.path}
                  className={`block text-gray-800 font-semibold hover:bg-gray-100 p-2 rounded-md ${
                    location.pathname === item.path ? "font-bold" : ""
                  }`}
                >
                  {item.label}
                </a>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default NavigationMenu;
